// Лист черновика: сворачивается ОКНО, а не написанное.
//
// Колонка справа шириной 340 пикселей, развёрнутый экран — под полторы тысячи.
// Если лист тянуть за окном, то при сворачивании цифры в столбик схлопываются
// в нечитаемое, а при обратном разворачивании не возвращаются туда, где были.
// Поэтому лист имеет ПОСТОЯННЫЙ размер в пикселях, один и тот же в обоих
// состояниях, а меняется только окно, через которое на него смотрят. Написанное
// не двигается никогда — ни на пиксель.
//
// В свёрнутом виде окно стоит на центре листа. Это не произвол: центр — там,
// где пишут, потому что развёрнутый вид показывает лист целиком и начинают
// всегда с середины. Уехать в угол можно прокруткой, окно этого не запрещает.

import { useCallback, useLayoutEffect, useRef } from "react";

export function Sheet({
  width,
  height,
  visible,
  expanded,
  tabIndex,
  onKeyDown,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  className,
  sheetStyle,
  children,
}: {
  /** Размер листа в CSS-пикселях. Постоянен: от него зависят координаты. */
  width: number;
  height: number;
  /** Режим показан. Скрытый через `display:none` узел теряет прокрутку. */
  visible: boolean;
  expanded: boolean;
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  // Указатель обслуживается ОДНИМ узлом — окном. Захват указателя
  // (`setPointerCapture`) уводит последующие события на тот элемент, что его
  // взял, поэтому обработчики, развешанные по разным узлам, молча теряют
  // движение и отпускание: штрих начинается и не заканчивается никогда.
  onPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp?: (event: React.PointerEvent<HTMLDivElement>) => void;
  className?: string;
  sheetStyle?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const view = useRef<HTMLDivElement | null>(null);
  // Точка листа, на которую смотрят, в координатах листа. Пересчитывается от
  // прокрутки ученика и восстанавливается после каждой смены окна.
  const centre = useRef({ x: width / 2, y: height / 2 });

  const remember = useCallback((): void => {
    const el = view.current;
    if (!el || el.clientWidth === 0) return;
    centre.current = {
      x: el.scrollLeft + el.clientWidth / 2,
      y: el.scrollTop + el.clientHeight / 2,
    };
  }, []);

  // Смена окна: развернули, свернули, вернулись в режим. Браузер зажимает
  // выход за края сам, поэтому клампа тут нет.
  useLayoutEffect(() => {
    const el = view.current;
    if (!el || !visible) return;
    el.scrollLeft = centre.current.x - el.clientWidth / 2;
    el.scrollTop = centre.current.y - el.clientHeight / 2;
  }, [visible, expanded, width, height]);

  return (
    <div
      ref={view}
      className={`scratch__view${className ? ` ${className}` : ""}`}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onScroll={remember}
    >
      <div
        className="scratch__sheet"
        style={{ width, height, position: "relative", ...sheetStyle }}
      >
        {children}
      </div>
    </div>
  );
}
